import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { TRANSLATIONS, Language, DEFAULT_LANGUAGE } from '@core/constants/translations';
import { LocalStorageService } from './local-storage.service';

export type { Language };

@Injectable({
  providedIn: 'root'
})
export class TranslateService {
  private currentLanguage$ = new BehaviorSubject<Language>(DEFAULT_LANGUAGE);
  private languageChanged$ = new Subject<Language>();
  private readonly LANGUAGE_KEY = 'app_language';

  constructor(private localStorageService: LocalStorageService) {
    this.initLanguage();
  }

  private initLanguage(): void {
    const savedLanguage = this.localStorageService.getLocalData(this.LANGUAGE_KEY);
    const normalized = this.normalizeLanguage(savedLanguage);
    this.currentLanguage$.next(normalized);
    this.localStorageService.setLocalData(this.LANGUAGE_KEY, normalized);
  }

  private isValidLanguage(lang: string): lang is Language {
    return ['ja', 'en'].includes(lang);
  }

  private normalizeLanguage(lang?: string | null): Language {
    const normalized = (lang ?? '').toString().trim().toLowerCase();
    if (normalized === 'jp' || normalized === 'ja') {
      return 'ja';
    }
    if (normalized === 'en' || normalized === 'eng') {
      return 'en';
    }
    return DEFAULT_LANGUAGE;
  }

  setLanguage(language: Language): void {
    const normalized = this.normalizeLanguage(language);
    this.currentLanguage$.next(normalized);
    this.languageChanged$.next(normalized);
    this.localStorageService.setLocalData(this.LANGUAGE_KEY, normalized);
  }

  getCurrentLanguage(): Language {
    return this.currentLanguage$.value;
  }

  getCurrentLanguage$(): Observable<Language> {
    return this.currentLanguage$.asObservable();
  }

  getLanguageChanged$(): Observable<Language> {
    return this.languageChanged$.asObservable();
  }

  translate(key: string, params?: any): string {
    const currentLang = this.normalizeLanguage(this.getCurrentLanguage());
    const translations = TRANSLATIONS[currentLang] ?? TRANSLATIONS[DEFAULT_LANGUAGE];
    const normalizedKey = (key ?? '').toString().trim();
    if (!normalizedKey) {
      return key;
    }

    let translation = translations[normalizedKey as keyof typeof translations];
    const hasKey =
      Object.prototype.hasOwnProperty.call(TRANSLATIONS.ja, normalizedKey) ||
      Object.prototype.hasOwnProperty.call(TRANSLATIONS.en, normalizedKey);

    if (translation == null) {
      // Fallback to Japanese or English if key not found in current language
      const fallbackJa = (TRANSLATIONS.ja as Record<string, string>)[normalizedKey];
      const fallbackEn = (TRANSLATIONS.en as Record<string, string>)[normalizedKey];
      translation = fallbackJa ?? fallbackEn;
    }

    if (translation == null) {
      if (!hasKey) {
        console.warn(`Translation key not found: ${normalizedKey}`);
      }
      return normalizedKey;
    }

    // Replace parameters {0}, {1}, etc.
    if (params) {
      if (Array.isArray(params)) {
        params.forEach((param, index) => {
          translation = translation.replace(`{${index}}`, param);
        });
      } else if (typeof params === 'object') {
        Object.keys(params).forEach(paramKey => {
          translation = translation.replace(`{${paramKey}}`, params[paramKey]);
        });
      }
    }

    return translation;
  }

  instant(key: string, params?: any): string {
    return this.translate(key, params);
  }
}
